import { apiClient } from './api-client';

// Article types
export type ArticleStatus = 'draft' | 'published' | 'archived';

export interface Article {
  id: string;
  tenantId: string;
  categoryId?: string;
  title: string;
  slug: string;
  content: string;
  contentHtml: string;
  excerpt?: string;
  status: ArticleStatus;
  authorId?: string;
  authorName?: string;
  metaTitle?: string;
  metaDescription?: string;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  // On `kb_articles` since the embedding pipeline shipped, but never declared
  // here. 12a's USED BY THE AI column is the first thing to read them: an
  // article the assistant cannot see is a defect no CMS surfaces.
  embeddingStatus?: 'pending' | 'processing' | 'completed' | 'failed' | 'stale';
  embeddedAt?: string;
  chunkCount?: number;
  isPublic: boolean;
  isFeatured: boolean;
  tags: string[];
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  category?: Category;
}

export interface ArticleVersion {
  id: string;
  articleId: string;
  version: number;
  title: string;
  content: string;
  authorId?: string;
  authorName?: string;
  changeNote?: string;
  createdAt: string;
}

// Category types
export interface Category {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  parentId?: string;
  sortOrder: number;
  isPublic: boolean;
  isActive: boolean;
  articleCount?: number;
  children?: Category[];
  createdAt: string;
  updatedAt: string;
}

// Filter and DTO types
export interface ArticleFilter {
  status?: ArticleStatus;
  categoryId?: string;
  tags?: string[];
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateArticleDto {
  title: string;
  content: string;
  categoryId?: string;
  excerpt?: string;
  metaTitle?: string;
  metaDescription?: string;
  tags?: string[];
  isPublic?: boolean;
  isFeatured?: boolean;
}

export interface UpdateArticleDto {
  title?: string;
  content?: string;
  // null clears the category ("No category"); '' is rejected by the backend
  // @IsUUID validation, so use null to disconnect (bug #9619).
  categoryId?: string | null;
  excerpt?: string;
  status?: ArticleStatus;
  metaTitle?: string;
  metaDescription?: string;
  tags?: string[];
  isPublic?: boolean;
  isFeatured?: boolean;
}

export interface CategoryFilter {
  parentId?: string;
  isPublic?: boolean;
  isActive?: boolean;
}

export interface CreateCategoryDto {
  name: string;
  description?: string;
  parentId?: string;
  icon?: string;
  isPublic?: boolean;
  sortOrder?: number;
}

export interface UpdateCategoryDto {
  name?: string;
  description?: string;
  parentId?: string;
  icon?: string;
  isPublic?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

export interface SearchQuery {
  query: string;
  type?: 'all' | 'articles' | 'categories';
  categoryId?: string;
  tags?: string[];
  page?: number;
  limit?: number;
}

export interface SearchResult {
  articles: Article[];
  categories: Category[];
  suggestions: string[];
  total: number;
  page: number;
  limit: number;
}

// Document types for PDF uploads
export interface KnowledgeDocument {
  id: string;
  tenantId: string;
  title: string;
  filename: string;
  mimeType: string;
  size: number;
  url?: string;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  categoryId?: string;
  extractedText?: string;
  pageCount?: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const knowledgeService = {
  // ============== ARTICLES ==============

  async getArticles(filter?: ArticleFilter): Promise<PaginatedResponse<Article>> {
    const params = new URLSearchParams();
    if (filter) {
      if (filter.status) params.append('status', filter.status);
      if (filter.categoryId) params.append('categoryId', filter.categoryId);
      if (filter.tags?.length) filter.tags.forEach((t) => params.append('tags', t));
      if (filter.search) params.append('search', filter.search);
      if (filter.page) params.append('page', String(filter.page));
      if (filter.limit) params.append('limit', String(filter.limit));
    }
    const response = await apiClient.get(`/articles?${params.toString()}`);
    return response.data;
  },

  async getPublishedArticles(page = 1, limit = 20): Promise<PaginatedResponse<Article>> {
    const response = await apiClient.get(`/articles/published?page=${page}&limit=${limit}`);
    return response.data;
  },

  async getFeaturedArticles(): Promise<Article[]> {
    const response = await apiClient.get('/articles/featured');
    return response.data;
  },

  async getArticle(id: string): Promise<Article> {
    const response = await apiClient.get(`/articles/${id}`);
    return response.data;
  },

  async getArticleBySlug(slug: string): Promise<Article> {
    const response = await apiClient.get(`/articles/slug/${slug}`);
    return response.data;
  },

  async getRelatedArticles(id: string, limit = 5): Promise<Article[]> {
    const response = await apiClient.get(`/articles/${id}/related?limit=${limit}`);
    return response.data;
  },

  async getArticleVersions(id: string): Promise<ArticleVersion[]> {
    const response = await apiClient.get(`/articles/${id}/versions`);
    return response.data;
  },

  async createArticle(dto: CreateArticleDto): Promise<Article> {
    const response = await apiClient.post('/articles', dto);
    return response.data;
  },

  async updateArticle(id: string, dto: UpdateArticleDto): Promise<Article> {
    const response = await apiClient.put(`/articles/${id}`, dto);
    return response.data;
  },

  async publishArticle(id: string): Promise<Article> {
    const response = await apiClient.post(`/articles/${id}/publish`);
    return response.data;
  },

  async unpublishArticle(id: string): Promise<Article> {
    const response = await apiClient.post(`/articles/${id}/unpublish`);
    return response.data;
  },

  async archiveArticle(id: string): Promise<Article> {
    const response = await apiClient.post(`/articles/${id}/archive`);
    return response.data;
  },

  async recordView(id: string): Promise<void> {
    await apiClient.post(`/articles/${id}/view`);
  },

  async submitFeedback(id: string, isHelpful: boolean, comment?: string): Promise<void> {
    await apiClient.post(`/articles/${id}/feedback`, { isHelpful, comment });
  },

  async deleteArticle(id: string): Promise<void> {
    await apiClient.delete(`/articles/${id}`);
  },

  // ============== CATEGORIES ==============

  async getCategories(filter?: CategoryFilter): Promise<Category[]> {
    const params = new URLSearchParams();
    if (filter) {
      if (filter.parentId) params.append('parentId', filter.parentId);
      if (filter.isPublic !== undefined) params.append('isPublic', String(filter.isPublic));
      if (filter.isActive !== undefined) params.append('isActive', String(filter.isActive));
    }
    const response = await apiClient.get(`/categories?${params.toString()}`);
    return response.data;
  },

  async getCategoryTree(): Promise<Category[]> {
    const response = await apiClient.get('/categories/tree');
    return response.data;
  },

  async getPublicCategoryTree(): Promise<Category[]> {
    const response = await apiClient.get('/categories/public/tree');
    return response.data;
  },

  async getCategory(id: string): Promise<Category> {
    const response = await apiClient.get(`/categories/${id}`);
    return response.data;
  },

  async getCategoryBySlug(slug: string): Promise<Category> {
    const response = await apiClient.get(`/categories/slug/${slug}`);
    return response.data;
  },

  async getCategoryBreadcrumb(id: string): Promise<Category[]> {
    const response = await apiClient.get(`/categories/${id}/breadcrumb`);
    return response.data;
  },

  async createCategory(dto: CreateCategoryDto): Promise<Category> {
    const response = await apiClient.post('/categories', dto);
    return response.data;
  },

  async updateCategory(id: string, dto: UpdateCategoryDto): Promise<Category> {
    const response = await apiClient.put(`/categories/${id}`, dto);
    return response.data;
  },

  async updateCategorySortOrder(items: Array<{ id: string; sortOrder: number }>): Promise<void> {
    await apiClient.put('/categories/sort-order', { items });
  },

  async deleteCategory(id: string): Promise<void> {
    await apiClient.delete(`/categories/${id}`);
  },

  // ============== SEARCH ==============

  async search(query: SearchQuery): Promise<SearchResult> {
    const params = new URLSearchParams();
    params.append('query', query.query);
    if (query.type) params.append('type', query.type);
    if (query.categoryId) params.append('categoryId', query.categoryId);
    if (query.tags?.length) query.tags.forEach((t) => params.append('tags', t));
    if (query.page) params.append('page', String(query.page));
    if (query.limit) params.append('limit', String(query.limit));
    const response = await apiClient.get(`/search?${params.toString()}`);
    return response.data;
  },

  async getSuggestions(query: string, limit = 5): Promise<string[]> {
    const response = await apiClient.get(
      `/search/suggestions?query=${encodeURIComponent(query)}&limit=${limit}`
    );
    return response.data;
  },

  async getPopularSearches(limit = 10, days = 30): Promise<Array<{ query: string; count: number }>> {
    const response = await apiClient.get(`/search/popular?limit=${limit}&days=${days}`);
    return response.data;
  },

  async logSearchClick(query: string, articleId: string): Promise<void> {
    await apiClient.post('/search/click', { query, articleId });
  },

  // ============== DOCUMENTS ==============

  async getDocuments(categoryId?: string): Promise<KnowledgeDocument[]> {
    const params = categoryId ? `?categoryId=${categoryId}` : '';
    const response = await apiClient.get(`/knowledge/documents${params}`);
    return response.data;
  },

  async getDocument(id: string): Promise<KnowledgeDocument> {
    const response = await apiClient.get(`/knowledge/documents/${id}`);
    return response.data;
  },

  async uploadDocument(
    file: File,
    onProgress?: (progress: number) => void,
    categoryId?: string,
  ): Promise<KnowledgeDocument> {
    const formData = new FormData();
    formData.append('file', file);
    if (categoryId) {
      formData.append('categoryId', categoryId);
    }

    const response = await apiClient.post('/knowledge/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  },

  async deleteDocument(id: string): Promise<void> {
    await apiClient.delete(`/knowledge/documents/${id}`);
  },

  /** Assign or clear a document's category. Pass null to clear. */
  async updateDocumentCategory(id: string, categoryId: string | null): Promise<KnowledgeDocument> {
    const response = await apiClient.patch(`/knowledge/documents/${id}`, { categoryId });
    return response.data;
  },

  async getDocumentDownloadUrl(id: string): Promise<{ url: string }> {
    const response = await apiClient.get(`/knowledge/documents/${id}/download-url`);
    return response.data;
  },

  /**
   * Get the streaming download path for a document
   * Use this with fileService.downloadAttachment() for authenticated downloads
   */
  getDocumentDownloadPath(id: string): string {
    return `/api/knowledge/documents/${id}/download`;
  },
};

export default knowledgeService;
